import React, { useState, useEffect } from "react";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import InventoryNav from "../components/inventoryNav";
import Time from "../components/time";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

const PurchasesreqView = () => {
  useAuthRedirect();
  const [details, setDetails] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);

  // ✅ Track freight inputs row-wise
  const [freightInputs, setFreightInputs] = useState({});

  /////////////////////////////////////////////////////////////
  const canView = hasPermission("Prior Purchase Requisition", "view");
  const canEntry = hasPermission("Prior Purchase Requisition", "entry");
  const canEdit = hasPermission("Prior Purchase Requisition", "edit");
  const canDel = hasPermission("Prior Purchase Requisition", "del");
  /////////////////////////////////////////////////////////////

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/prPurchase_details");
      const { details = [], headers = [] } = response.data;

      if (details.length === 0) {
        console.warn("No details found.");
        setDetails([]);
        setHeaders([]);
        setFilteredData([]);
        setLoading(false);
        return;
      }

      setDetails(details);
      setHeaders(headers);
      setFilteredData(details);
    } catch (error) {
      console.error("Error fetching details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = e.target.value.toLowerCase();
    setSearchTerm(term);

    if (term === "") {
      setFilteredData(details);
      return;
    }

    const filtered = details.filter(
      (item) =>
        String(item.prDetail_number || "").toLowerCase().includes(term) ||
        String(item.prDetail_supplierCode || "").toLowerCase().includes(term) ||
        String(item.branchcode || "").toLowerCase().includes(term) ||
        String(item.supplierMaster_name || "").toLowerCase().includes(term)
    );

    setFilteredData(filtered);
  };

  // Find Matching Header for Indent No
  // ✅ Find Matching Header for Indent No and Freight
const findMatchingHeader = (prDetail_number) => {
  const matchingHeader = headers.find(
    (header) =>
      String(header.prHeader_number).trim() ===
      String(prDetail_number).trim()
  );

  if (!matchingHeader) {
    return { indentNumber: "Not Found", freight: null, branch: null };
  }

  return {
    indentNumber: matchingHeader.prHeader_indentNumber,
    freight: matchingHeader.freight_charges,
    branch: matchingHeader.branchCode,
  };
};

  // ✅ Save Freight Charges
  const handleSaveFreight = async (prNo, branch) => {
    const freightValue = freightInputs[prNo];

    if (!freightValue || isNaN(Number(freightValue)) || Number(freightValue) < 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Freight",
        text: "Please enter a valid positive number for freight charges.",
      });
      return;
    }

    try {
      Swal.fire({
        title: "Submitting...",
        text: "Please wait while we save the freight charges.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await axios.put("https://rfs2.infraconsoft.net.in/api/purchaseReq_frightInsert", {
        fright: Number(freightValue),
        branch,
        prNo,
      });

      Swal.fire({
        icon: "success",
        title: "Saved!",
        text: "Freight charges added successfully.",
      });

      // ✅ Refresh after save
      fetchData();
    } catch (error) {
      console.error("Error saving freight:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to save freight charges.",
      });
    }
  };

  if (!canView) {
    return (
      <div id="admin-container">
        <InventoryNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  // Pagination logic
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  const handleDelete = async (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          Swal.fire({
            title: "Submitting...",
            text: "Please wait while we process your request.",
            allowOutsideClick: false,
            didOpen: () => {
              Swal.showLoading();
            },
          });

          await axios.delete(`https://rfs2.infraconsoft.net.in/api/purchaseReq_delete/${id}`);

          // ✅ Refresh entire list from backend
          fetchData();

          Swal.fire({
            title: "Deleted!",
            text: "Your record has been deleted.",
            icon: "success",
          });
        } catch (error) {
          console.error("Error deleting record:", error);
          Swal.fire({
            title: "Error!",
            text: "Failed to delete record.",
            icon: "error",
          });
        }
      }
    });
  };

  return (
    <div id="admin-container">
      <InventoryNav />
      <div className="content-right">
        <section>
          <h2 className="add-txt">Prior Purchase Requisition</h2>
          <Time />
        </section>

        {canEntry && (
          <div className="next-container">
            <Link to={`/inventory/prApprovalView`}>
              <button className="next-btn">
                Next Prior Purchase Requisition Approval{" "}
                <i className="fa-solid fa-arrow-right"></i>
              </button>
            </Link>
          </div>
        )}

        {/* Search Filter */}
        <div className="search-filter">
          <p>Search: </p>
          <input
            type="text"
            placeholder="Search by Req No"
            value={searchTerm}
            onChange={handleSearch}
            className="search-input"
          />
        </div>

        {/* Table */}
        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>PriPurchaseReq No</th>
                <th>Branch</th>
                <th>Supplier Code</th>
                <th>Supplier Name</th>
                {canEntry && <th>Freight</th>}
                <th>Indent No</th>
                {canDel && <th>Delete</th>}
              </tr>
            </thead>
            <tbody>
  {loading ? (
    <tr>
      <td colSpan="8" className="loading-text">
        Loading data, please wait...
      </td>
    </tr>
  ) : currentData.length > 0 ? (
    currentData.map((item, index) => {
      const headerInfo = findMatchingHeader(item.prDetail_number);

      return (
        <tr key={index}>
          <td>{indexOfFirstItem + index + 1}</td>

          {/* PR Detail Number */}
          <td>
            <Link to={`/inventory/prPurchaseReqviewsingle/${item.prDetail_number}`}>
              {item.prDetail_number}
            </Link>
          </td>

          <td>{item.branchcode}</td>
          <td>{item.prDetail_supplierCode}</td>
          <td>{item.supplierMaster_name}</td>

          {/* Freight Column */}
          {canEntry && (
            <td>
              {!headerInfo.freight ? (
                <form
  onSubmit={async (e) => {
    e.preventDefault();
    const freightValue = e.target.freight.value;

    try {
      Swal.fire({
                  title: "Submitting...",
                  text: "Please wait while we process your request.",
                  allowOutsideClick: false,
                  didOpen: () => {
                      Swal.showLoading();
                  },
              });
      await axios.put("https://rfs2.infraconsoft.net.in/api/purchaseReq_frightInsert", {
        fright: freightValue,
        branch: headerInfo.branch,
        prNo: item.prDetail_number,
      });

      Swal.fire("Success", "Freight inserted successfully", "success");
      fetchData();
    } catch (error) {
      console.error("Freight insert error:", error);
      Swal.fire("Error", "Failed to insert freight", "error");
    }
  }}
  style={{
    display: "flex",
    alignItems: "center",
    gap: "6px",
  }}
>
  <input
    type="text"
    name="freight"
    placeholder="Enter Freight"
    style={{
      width: "100px",
      padding: "4px 6px",
      border: "1px solid #ccc",
      borderRadius: "4px",
      fontSize: "13px",
    }}
  />
  <button
    type="submit"
    style={{
      padding: "5px 10px",
      backgroundColor: "#4CAF50",
      color: "white",
      border: "none",
      borderRadius: "4px",
      fontSize: "13px",
      cursor: "pointer",
    }}
  >
    Save
  </button>
</form>

              ) : (
                <span>{headerInfo.freight}</span>
              )}
            </td>
          )}

          {/* Indent Number (from headerInfo object) */}
          <td>{headerInfo.indentNumber}</td>

          {/* Delete Column */}
          {canDel && (
            <td className="del">
              <i
                className="fa-solid fa-trash-can"
                onClick={() => handleDelete(item.prDetail_number)}
              ></i>
            </td>
          )}
        </tr>
      );
    })
  ) : (
    <tr>
      <td colSpan="8">No Data Available</td>
    </tr>
  )}
</tbody>

          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>

          <button
            onClick={() => paginate(1)}
            className={currentPage === 1 ? "active" : ""}
          >
            1
          </button>

          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map(
              (_, index) => {
                const pageNumber = currentPage + index;
                if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => paginate(pageNumber)}
                      className={currentPage === pageNumber ? "active" : ""}
                    >
                      {pageNumber}
                    </button>
                  );
                }
                return null;
              }
            )}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchasesreqView;
